import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ElementRef, forwardRef, Injector, inject, input, OnDestroy, Renderer2, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { IconComponent } from "../../../../../../util-icons/src/lib/components/icon/icon.component";



@Component({
  selector: 'lib-custom-date-picker',
  standalone: true,
  imports: [CommonModule, 
    IconComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './custom-date-picker.component.html',
  styleUrl: './custom-date-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDatePickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CustomDatePickerComponent),
      multi: true
    },
  ],
})
export class CustomDatePickerComponent implements ControlValueAccessor, OnDestroy {
  label = input<string>('');
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  value: string | null = null;
  control: FormControl | null = null; // Add FormControl reference
  displayValue: string | null = null; // For displaying in MM/DD/YYYY format
  showPicker = false;
  year: number;
  month: number;
  daysInMonth: (number | null)[];
  daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  @ViewChild('calendar', { static: false }) calendar!: ElementRef<HTMLDivElement>;
  @ViewChild('inputGroup', { static: false }) inputGroup!: ElementRef<HTMLDivElement>;

  constructor(
    private cdr: ChangeDetectorRef, 
    private elementRef: ElementRef,
    private injector: Injector,
    private renderer: Renderer2,
  ) {
    const today = new Date();
    this.year = today.getFullYear();
    this.month = today.getMonth();
    this.daysInMonth = this.getDaysInMonth(this.year, this.month);
    this.value = null;
    this.displayValue = this.value ? this.formatDateForDisplay(this.value) : null;

  }

  ngAfterContentInit(): void {
    // Retrieve the parent FormControl
    const ngControl = this.injector.get(NgControl, null) as NgControl;
    if (ngControl) {
      this.control = ngControl.control as FormControl;
    } else {
      this.control = new FormControl(null); // Fallback
    }
    // Ensure validators are applied
    this.updateControlValue();
  }

  ngAfterViewChecked(): void {
    if (this.showPicker && this.calendar && this.inputGroup) {
      this.positionCalendar();
    }
  }

  togglePicker(event: MouseEvent): void {
    event.stopPropagation(); // Prevent click from triggering click-outside
    this.showPicker = !this.showPicker;
    if (this.showPicker) {
      this.addClickOutsideListener();
      this.positionCalendar();
    } else {
      this.removeClickOutsideListener();
      // Reset position when closing to avoid stale styles
      if (this.calendar) {
        this.renderer.removeStyle(this.calendar.nativeElement, 'top');
        this.renderer.removeStyle(this.calendar.nativeElement, 'left');
        this.renderer.setStyle(this.calendar.nativeElement, 'visibility', 'hidden');
      }
    }
    this.cdr.markForCheck();
  }

  get monthName(): string {
    return this.monthNames[this.month];
  }

  previousMonth(): void {
    this.month--;
    if (this.month < 0) {
      this.month = 11;
      this.year--;
    }
    this.daysInMonth = this.getDaysInMonth(this.year, this.month);
    this.cdr.markForCheck();
  }
    
  nextMonth(): void {
    this.month++;
    if (this.month > 11) {
      this.month = 0;
      this.year++;
    }
    this.daysInMonth = this.getDaysInMonth(this.year, this.month);
    this.cdr.markForCheck();
  }

  selectDate(day: number | null): void {
    if (!day) return;
    const dateString = `${this.year}-${String(this.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    this.value = dateString;
    this.displayValue = this.formatDateForDisplay(dateString);
    this.control?.setValue(dateString, { emitEvent: true });
    this.control?.markAsTouched();
    this.onChange(dateString);
    this.showPicker = false;
    this.removeClickOutsideListener();
    this.cdr.markForCheck();
  }

  isSelected(day: number | null): boolean {
    if (!day || !this.value) return false;
    const [year, month, selectedDay] = this.value.split('-').map(Number);
    return year === this.year && month - 1 === this.month && selectedDay === day;
  }


  // Private methods
  private getDaysInMonth(year: number, month: number): (number | null)[] {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: (number | null)[] = Array(firstDay).fill(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  }

  // Convert Date to YYYY-MM-DD
  private formatDateToYYYYMMDD(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  // Convert Date to MM/DD/YYYY
  private formatDateForDisplay(date: string): string {
    return `${date.split('-')[1]}/${date.split('-')[2]}/${date.split('-')[0]}`;
  }

  private isValidDate(value: string | null): boolean {
    if (!value) return false;
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(value)) return false;
    const [year, month, day] = value.split('-').map(Number);
    if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) return false;
    const daysInMonth = new Date(year, month, 0).getDate();
    return day <= daysInMonth;
  }

  private positionCalendar(): void {
      if (!this.calendar || !this.inputGroup) return;
      const inputRect = this.inputGroup.nativeElement.getBoundingClientRect();
      const calendar = this.calendar.nativeElement;
      const calendarRect = calendar.getBoundingClientRect();

      // Default: below the input, aligned left
      let top = inputRect.bottom + 8;
      let left = inputRect.right - calendarRect.width; // Align right edge, with 8px padding

      // Prevent overflow right
      if (left + calendarRect.width > window.innerWidth - 8) {
        left = window.innerWidth - calendarRect.width - 8;
      }
      // Prevent overflow left
      if (left < 8) {
        left = 8;
      }
      // Prevent overflow bottom
      if (top + calendarRect.height > window.innerHeight - 8) {
        top = inputRect.top - calendarRect.height - 8;
      }
      // Prevent overflow top
      if (top < 8) {
        top = 8;
      }

      this.renderer.setStyle(calendar, 'top', `${top}px`);
      this.renderer.setStyle(calendar, 'left', `${left}px`);
      this.renderer.setStyle(calendar, 'visibility', 'visible'); // Show after positioning
  }


  private addClickOutsideListener(): void {
    this.removeClickOutsideListener();
    this.clickOutsideListener = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!this.elementRef.nativeElement.contains(target)) {
        this.showPicker = false;
        this.removeClickOutsideListener();
        this.cdr.markForCheck();
      }
    };
    document.addEventListener('click', this.clickOutsideListener);
  }

  private removeClickOutsideListener(): void {
    if (this.clickOutsideListener) {
      document.removeEventListener('click', this.clickOutsideListener);
      this.clickOutsideListener = null;
      this.cdr.markForCheck();
    }
  }

  // Helper to update FormControl value
  private updateControlValue(): void {
    if (this.control && this.value !== this.control.value) {
      this.control.setValue(this.value, { emitEvent: true });
    }
    this.onChange(this.value);
  }


  // ControlValueAccessor methods
  writeValue(value: string | Date | null): void {
    let dateString: string | null = null;
    if (typeof value === 'string' && this.isValidDate(value)) {
      dateString = value; // Already in YYYY-MM-DD
    } else if (value instanceof Date && !isNaN(value.getTime())) {
      dateString = this.formatDateToYYYYMMDD(value);
    }
    this.value = dateString;
    this.displayValue = dateString ? this.formatDateForDisplay(dateString) : null;
    this.updateControlValue();
    if (dateString && this.isValidDate(dateString)) {
      const [year, month] = dateString.split('-').map(Number);
      this.year = year;
      this.month = month - 1;
      this.daysInMonth = this.getDaysInMonth(this.year, this.month);
    }
    this.cdr.markForCheck();
  }

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private clickOutsideListener: ((event: MouseEvent) => void) | null = null;

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.trim();
    let dateString: string | null = null;
    // Try parsing as MM/DD/YYY
    if (value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [month, day, year] = value.split('/').map(Number);
      dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      if (!this.isValidDate(dateString)) {
        dateString = null;
      }
    } else if (this.isValidDate(value)) {
      dateString = value; // Already in YYYY-MM-DD
    }
    this.value = dateString;
    this.displayValue = dateString ? this.formatDateForDisplay(dateString) : value || null;
    this.control?.setValue(dateString, { emitEvent: true });
    this.onChange(dateString);
    this.cdr.markForCheck();
  }

  onBlur(): void {
    this.onTouched();    
    if (this.control) {
      this.control.markAsTouched();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return control.valid ? null : control.errors;
  }
  
  ngOnDestroy(): void {
    this.removeClickOutsideListener();
  }
@Input() isDateSelectableFn?: (day: number | null, year: number, month: number) => boolean;

isDateSelectable(day: number | null): boolean {
  if (!day) return false;

  const date = new Date(this.year, this.month, day);

  if (this.minDate) {
    const min = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate());
    if (date < min) return false;
  }

  if (this.maxDate) {
    const max = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate());
    if (date > max) return false;
  }

  if (this.isDateSelectableFn) {
    return this.isDateSelectableFn(day, this.year, this.month);
  }
  return true;
}


}